import { DocumentModel } from 'shared/domain/document/documentModel';
import { SyncStatus } from 'shared/domain/entitySyncStatus/syncStatus';
import {
  PostFile,
  uploadFile,
} from 'serviceWorker/repository/file/postFile';
import { isAbortError } from 'serviceWorker/repository/httpRequest/abortRequest';
import {
  broadcastFailUploadedDocument,
  broadcastUploadedDocument,
} from 'serviceWorker/broadcasts/documents';
import {
  AddRemoteIdToDocument,
  addRemoteIdToDocument,
} from './addRemoteIdToDocument';
import { hardRemoveDocument } from './hardRemoveDocument';
import { setDocumentSync, SetDocumentSync } from './setDocumentSync';
import { updateOne as updateDocument } from 'serviceWorker/db/documents';
import { confirmUpload, failUpload } from './uploadConfirm';
import { debugLog } from 'shared/logger/debugLog';
import { UploadStatus } from 'shared/types/uploadStatus';
import { MIME_TYPES } from 'shared/constants/MIME_TYPES';
import { resolveMimeTypeFromFileName } from 'shared/utils/fileExtensions';
import {
  LOCAL_DOCUMENT_ERROR_MESSAGES,
  LOCAL_DOCUMENT_ERROR_CODES,
} from 'shared/utils/errorHandling/constants';
import { createErrorWithCode } from 'shared/utils/errorHandling';
import { revokeBlobUrlsSafely } from 'shared/utils/blobUrl';

export interface IDocumentUploader {
  POST: (url: string, data: any) => Promise<any>;
  GET: (url: string) => Promise<any>;
  DELETE: (url: string) => Promise<any>;
  POST_FILE: PostFile;
  PUT: (url: string, data: any) => Promise<any>;
}

export type UploadDocument = (
    abortController: AbortController,
    uploader: IDocumentUploader,
    document: DocumentModel,
    url: string
  ) => Promise<boolean>;

// export for testing purposes
export function makeUploadDocument(
  addRemoteIdToDocument: AddRemoteIdToDocument,
  setDocumentSync: SetDocumentSync
): UploadDocument {
  const createLocalDocumentDataInaccessibleError = (): Error =>
    createErrorWithCode(
      LOCAL_DOCUMENT_ERROR_MESSAGES.DATA_INACCESSIBLE,
      LOCAL_DOCUMENT_ERROR_CODES.LOCAL_DOCUMENT_DATA_INACCESSIBLE
    );

  return async function _uploadDocument(
    abortController: AbortController,
    uploader: IDocumentUploader,
    document: DocumentModel,
    url: string
  ): Promise<boolean> {
    const localDataUrl = document.localData;
    if (!localDataUrl) {
      throw createLocalDocumentDataInaccessibleError();
    }
    const imageData: Response = await fetch(localDataUrl).catch(
      async () => {
        revokeBlobUrlsSafely([localDataUrl]);
        await hardRemoveDocument(document.localId);
        throw createLocalDocumentDataInaccessibleError();
      }
    );
    const imageBlob: Blob = await imageData.blob();
    const fileName = ensureFileName(document.data?.name);
    const fileType = ensureFileType(document.type, document.data?.name);
    const file = new File([imageBlob], fileName, {
      type: fileType,
    });

    const signedData = await getSignedUrlForUpload(
      uploader,
      url,
      document
    );
    await addRemoteIdToDocument(document.localId, signedData.document._id);
    try {
      const uploadResponse = await uploadFile(
        abortController,
        uploader.POST_FILE,
        signedData.signedRequest.fields,
        signedData.signedRequest.url,
        file
      );
      if (!uploadResponse.ok) {
        throw new Error(
          `Upload failed with status: ${uploadResponse.status}`
        );
      }
      const confirmation = await confirmUpload(
        uploader,
        `${url}/${signedData.document._id}`
      );
      if (confirmation?.isExportAllowed !== undefined) {
        await updateDocument(
          document.localId,
          { isExportAllowed: confirmation.isExportAllowed },
          { skipModifyTime: true }
        );
      }
      // getDocumentUrls gates fetching the real signed URL on this being
      // SUCCESS (calling downloadData before the backend has it gets a 409).
      // Nothing else in the initial-upload path ever sets it, so without
      // this the gallery is stuck resolving the local blob for the rest of
      // the session even though the S3 upload has fully completed.
      await updateDocument(
        document.localId,
        { data: { uploadStatus: UploadStatus.success } },
        { skipModifyTime: true }
      );
      await setDocumentSync(document.localId, SyncStatus.SUCCESS);
      revokeBlobUrlsSafely([localDataUrl]);
      broadcastUploadedDocument({
        localId: document.localId,
        _id: signedData.document._id,
      });
      return true;
    } catch (e) {
      debugLog('Upload failed:', e);
      await setDocumentSync(document.localId, SyncStatus.FAIL);
      await failUpload(uploader, `${url}/${signedData.document._id}`);
      if (isAbortError(e)) {
        revokeBlobUrlsSafely([localDataUrl]);
        await hardRemoveDocument(document.localId);
      }
      broadcastFailUploadedDocument(document.localId);
    }

    return false;
  };
}

export const uploadDocument: UploadDocument = makeUploadDocument(
  addRemoteIdToDocument,
  setDocumentSync
);

function getSignedUrlForUpload(
  uploader: IDocumentUploader,
  url: string,
  file: any
): Promise<any> {
  const body = {
    description: file.description,
    fileName: ensureFileName(file.data?.name),
    fileType: ensureFileType(file.type, file.data?.name),
    title: file.title,
    imageRotation: file.imageRotation,
  };

  return uploader.POST(url, body);
}

function ensureFileName(fileName: string | undefined): string {
  return fileName || 'unknown_file';
}

export const FALLBACK_FILE_TYPE = MIME_TYPES.OCTET_STREAM;

export function ensureFileType(
  type: string | undefined,
  fileName?: string
): string {
  const trimmed = type?.trim();
  if (trimmed) return trimmed;
  return resolveMimeTypeFromFileName(fileName, FALLBACK_FILE_TYPE);
}
